import React, { useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  Checkbox,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@roger/ui-components';
import { renderInstructionFields } from '../../utils/goals';
import type { GoalTemplateOption } from '../../services/api/types.gen';
import DisciplineBadge from './DisciplineBadge';

interface GoalOptionCardProps {
  goalOption: GoalTemplateOption;
  selected?: boolean;
  onClick?: () => void;
  /**
   * Discipline pills to show next to the goal name. Only passed by the
   * external-EMR episode flow, where options span all disciplines; the
   * visit flow is already discipline-scoped and passes nothing.
   */
  disciplines?: string[];
}

const GoalOptionCard: React.FC<GoalOptionCardProps> = ({
  goalOption,
  selected = false,
  onClick,
  disciplines,
}) => {
  const [expanded, setExpanded] = useState(false);

  const handleChevronClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpanded(prev => !prev);
  };

  return (
    <Card
      className={`cursor-pointer border transition-colors ${
        selected
          ? 'border-primary bg-primary/5 ring-1 ring-primary/30'
          : 'hover:bg-muted/50'
      }`}
      onClick={onClick}
    >
      <CardContent className='px-2 py-1'>
        <div className='flex items-center gap-1.5'>
          <Checkbox checked={selected} className='mt-0.5 pointer-events-none' />
          <div className='flex flex-1 min-w-0 items-center gap-1.5'>
            {disciplines && disciplines.length > 0 && (
              <span className='flex shrink-0 items-center gap-1'>
                {disciplines.map(discipline => (
                  <DisciplineBadge key={discipline} discipline={discipline} />
                ))}
              </span>
            )}
            <p className='text-sm font-medium leading-tight line-clamp-2'>
              {goalOption.name}
            </p>
            {goalOption.num_attached_interventions >= 1 && (
              <p className='text-muted-foreground text-[11px] leading-tight shrink-0'>
                {goalOption.num_attached_interventions} intervention
                {goalOption.num_attached_interventions === 1 ? '' : 's'}
              </p>
            )}
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type='button'
                  variant='ghost'
                  size='sm'
                  onClick={handleChevronClick}
                  className='h-5 w-5 p-0 text-muted-foreground hover:text-foreground shrink-0'
                  aria-expanded={expanded}
                  aria-label={expanded ? 'Collapse details' : 'Expand details'}
                >
                  {expanded ? (
                    <ChevronDown className='h-4 w-4' />
                  ) : (
                    <ChevronRight className='h-4 w-4' />
                  )}
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                {expanded ? 'Hide details' : 'Show details'}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        {expanded && (
          <div className='mt-2 pl-7 text-sm text-muted-foreground'>
            {renderInstructionFields(goalOption.instruction)}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default GoalOptionCard;
